import { isCountryLevel, type AdminDestinationListItem } from "@ckgo2/shared";
import type { BatchFile, BatchFileStatus, BatchUpload } from "./upload/batchUpload.types";

/**
 * Pravidla hostitelů panelu dávky (vize podnety-tymu-2026-10, F7.8): stránka Média
 * (`MediaUploadArea`) a záložka Nahrát okna výběru fotek (`MediaModal` v `MediaPicker.tsx`).
 * Hostitel nečte zpětné volání modelu dávky: co vybrat a kdy obnovit seznam odvozuje ze stavu
 * souborů (`files`, `mediaId`) a z `startedAt`/`finishedAt` (Sémantika bod 2 v
 * `upload/batchUpload.types.ts`). Bez Reactu, `api` i `window`, ať jde testovat v `node`.
 */

/** Stavy, jejichž `mediaId` jde vybrat do pole editoru (nová, existující, první v dávce). */
const SELECTABLE_BATCH_STATUSES: ReadonlySet<BatchFileStatus> = new Set<BatchFileStatus>([
  "done",
  "duplicate-live",
  "duplicate-in-batch",
]);

/** Stavy, ve kterých model se souborem skončil (jen ty nese otisk dokončené dávky). */
const SETTLED_BATCH_STATUSES: ReadonlySet<BatchFileStatus> = new Set<BatchFileStatus>([
  "done",
  "duplicate-live",
  "duplicate-trash",
  "duplicate-in-batch",
  "error",
  "error-permanent",
]);

/** Hodnoty filtru země stránky, které nejsou ID destinace (`MediaListQuerySchema.country`). */
const NON_ID_COUNTRY_FILTERS: ReadonlySet<string> = new Set(["none", "any"]);

type CountryRow = Pick<AdminDestinationListItem, "id" | "parentId" | "showAsCountry">;

function selectableId(f: BatchFile): string | null {
  return SELECTABLE_BATCH_STATUSES.has(f.status) && f.mediaId ? f.mediaId : null;
}

/**
 * Země dávky z filtru stránky: bez filtru, `"none"` a `"any"` → `[]`; ID řádku seznamu
 * destinací zemské úrovně (`isCountryLevel`, totéž pravidlo jako `DestinationPicker.model`)
 * → `[ID]`; ID, které v seznamu není nebo není zemské úrovně (zastaralá nebo ručně upravená
 * adresa; server by každý soubor dávky odmítl kódem `media_country_not_country_level`) → `[]`;
 * konkrétní ID nad nenačteným seznamem (`rows` undefined) → `null` (panel počká).
 */
export function uploadCountriesFromFilter(
  country: string | undefined,
  rows: readonly CountryRow[] | undefined,
): string[] | null {
  if (!country || NON_ID_COUNTRY_FILTERS.has(country)) return [];
  if (rows === undefined) return null;
  const row = rows.find((r) => r.id === country);
  return row && isCountryLevel(row) ? [row.id] : [];
}

/**
 * Oblast nahrávání na stránce je vidět, když ji otevřelo tlačítko „Nahrát“, nebo když dávka
 * běžela a ještě nebyla vynulovaná (`startedAt !== null`): souhrn, chyby a „Zkusit znovu“
 * zůstanou vidět až do „Hotovo“, i když redaktor oblast během dávky schoval.
 */
export function uploadAreaVisible(open: boolean, b: Pick<BatchUpload, "startedAt">): boolean {
  return open || b.startedAt !== null;
}

/**
 * Otisk dokončeného stavu dávky: `null`, dokud dávka běží nebo nikdy neskončila; jinak řetězec
 * z `key:status:mediaId` souborů v koncových stavech (pořadí panelu). Hostitel obnoví seznam,
 * kdykoli se nenullová hodnota změní (konec dávky, konec opakování, obnova z koše po konci).
 */
export function batchSettledKey(
  b: Pick<BatchUpload, "files" | "running" | "finishedAt">,
): string | null {
  if (b.running || b.finishedAt === null) return null;
  return b.files
    .filter((f) => SETTLED_BATCH_STATUSES.has(f.status))
    .map((f) => `${f.key}:${f.status}:${f.mediaId ?? ""}`)
    .join("|");
}

/**
 * Výběr více fotek: ID vybratelných souborů, které ještě nebyly předány (`handled` = klíče
 * souborů); jen po startu dávky. Vrací ID v pořadí panelu bez opakování a klíče, které se tím
 * předaly. Fotky, které už ve výběru jsou, vyřadí až `unionIds` nad posledním výběrem editoru
 * (štítek „V galerii“, výběr se nemění).
 */
export function pickerAdditions(
  b: Pick<BatchUpload, "files" | "startedAt">,
  handled: ReadonlySet<string>,
): { ids: string[]; keys: string[] } {
  const ids: string[] = [];
  const keys: string[] = [];
  if (b.startedAt === null) return { ids, keys };
  for (const f of b.files) {
    if (handled.has(f.key)) continue;
    const id = selectableId(f);
    if (id === null) continue;
    keys.push(f.key);
    if (!ids.includes(id)) ids.push(id);
  }
  return { ids, keys };
}

/**
 * Jednoduchý výběr: ID, se kterým se okno zavře, nebo `null`. Jen po konci dávky
 * (`!running && finishedAt !== null`), bez souboru ve stavu `error` (opakovatelná chyba nechá
 * okno otevřené pro „Zkusit znovu“) a s aspoň jedním vybratelným souborem; první v pořadí panelu.
 */
export function singlePickerChoice(
  b: Pick<BatchUpload, "files" | "running" | "finishedAt">,
): string | null {
  if (b.running || b.finishedAt === null) return null;
  if (b.files.some((f) => f.status === "error")) return null;
  for (const f of b.files) {
    const id = selectableId(f);
    if (id !== null) return id;
  }
  return null;
}

/** Sjednocení výběru: `null`, když `add` nic nového nepřináší (editor se nevolá). */
export function unionIds(current: readonly string[], add: readonly string[]): string[] | null {
  const next = [...current];
  for (const id of add) if (!next.includes(id)) next.push(id);
  return next.length === current.length ? null : next;
}
